import React, { ComponentType, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { General, Shapes, Time } from 'icons/untitledui';
import { EventDetail, EventStatus } from 'events/_types';
import { EntityTag } from 'shiftNotes/components/EntityTag/_index';
import { collectVisibleEntityTags } from 'shiftNotes/helpers/entityTagUtils';
import { getUrgencyColors, getUrgencyLabel } from 'dashboard/urgencyColors';
import getEventUrgencyKey from 'events/helpers/getEventUrgencyKey';
import isEventResolved from 'events/helpers/isEventResolved';

import * as Styled from 'events/styles/EventSummary.style';
import * as Text from 'theme/components/Text.style';

const STATUS_ICONS: Record<EventStatus, ComponentType<{ size?: number }>> = {
  open: Shapes.Circle,
  resolved: General.CheckCircle,
  auto_resolved: Time.ClockCheck,
  invisible: General.EyeOff,
};

const KNOWN_STATUSES: EventStatus[] = [
  'open',
  'resolved',
  'auto_resolved',
  'invisible',
];

interface EventSummaryProps {
  event: EventDetail;
}

export default function EventSummary({
  event,
}: EventSummaryProps): JSX.Element {
  const { t } = useTranslation('pageEvents');

  const status: EventStatus = KNOWN_STATUSES.includes(
    event.status as EventStatus
  )
    ? (event.status as EventStatus)
    : 'open';
  const isPrivate = event.is_private;
  const displayStatus: EventStatus = isPrivate ? 'invisible' : status;
  const StatusIconComponent = isPrivate ? General.EyeOff : STATUS_ICONS[status];
  const statusLabel = isPrivate
    ? t('eventSummary.status.private')
    : t(`eventSummary.status.${status}`);

  const urgencyKey = useMemo(
    () => getEventUrgencyKey(event.shift_notes ?? []) ?? event.priority,
    [event.shift_notes, event.priority]
  );
  const priorityColors = getUrgencyColors(urgencyKey);
  const priorityLabel = getUrgencyLabel(urgencyKey, t);
  const showUrgencyTag = !isEventResolved(status);

  const allExtractions = useMemo(
    () =>
      (event.shift_notes ?? []).flatMap(
        (note) => note.entity_extractions ?? []
      ),
    [event.shift_notes]
  );

  const otherTags = useMemo(
    () => collectVisibleEntityTags(allExtractions),
    [allExtractions]
  );

  return (
    <Styled.Container>
      <Styled.StatusRow $status={displayStatus}>
        <StatusIconComponent size={16} aria-hidden="true" />
        <Text.P2 as="span">{statusLabel}</Text.P2>
      </Styled.StatusRow>
      <Text.H4>
        {event.summary || event.shift_notes?.[0]?.edited_text || ''}
      </Text.H4>
      {((priorityColors && showUrgencyTag) || otherTags.length > 0) && (
        <Styled.TagsRow>
          {priorityColors && priorityLabel && showUrgencyTag && (
            <EntityTag
              extractionClass="urgency"
              category={priorityLabel}
              description={null}
              colors={priorityColors}
            />
          )}
          {otherTags.map((tag) => (
            <EntityTag
              key={tag.key}
              extractionClass={tag.extractionClass}
              category={tag.category}
              description={tag.description}
            />
          ))}
        </Styled.TagsRow>
      )}
    </Styled.Container>
  );
}
